import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "environments/environment";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import * as L from "leaflet";
import { GlobalSeguridad } from "@seguridad";
import {
  construirDiscoverDesdeMetadataArcGis,
  esMetadataArcGisValida,
  esUrlArcGisRest,
  esUrlServicioValida,
  extraerTokenFiltroServicio,
  fusionarDiscoverConAsociadas,
  normalizarUrlDiscover,
  normalizarUrlServicioArcGis,
  respuestaDiscoverTieneCapas,
  sanearUrlServicioDiscover,
} from "./utils/arcgis-discover.util";

@Injectable({
  providedIn: "root",
})
export class ServiciosService {
  capas: any;
  mapaSeleccionado: any;

  constructor(private http: HttpClient, private seguridadGlobal: GlobalSeguridad) { }

  /**
   * Obtiene las capas asociadas a una url
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  DescubrirServidor(url: string, forzarActualizacion = false): Observable<any> {
    const urlNormalizada = normalizarUrlDiscover(sanearUrlServicioDiscover(url));

    if (!esUrlServicioValida(urlNormalizada)) {
      return of({ state: 'fail', layers: [], message: 'URL inválida' });
    }

    // Mismo contrato de producción: GET discoverLayer/{urlEncode}
    // (POST no existe en gam-layer → 404)
    const discover$ = this.descubrirServidorGet(urlNormalizada, forzarActualizacion);

    // ArcGIS: el inventario debe mostrar Asociadas (CAP_, campos, state) aunque
    // discoverLayer responda OK. Antes se devolvía el discover tal cual y la grilla
    // salía en blanco / sin code → no se podía desasociar.
    if (esUrlArcGisRest(urlNormalizada)) {
      return discover$.pipe(
        switchMap((respuesta) => {
          if (respuestaDiscoverTieneCapas(respuesta)) {
            return this.enriquecerDiscoverConAsociadasBd(
              respuesta,
              urlNormalizada,
              forzarActualizacion
            ).pipe(catchError(() => of(respuesta)));
          }
          return this.descubrirArcGisRestConAsociacionesBd(urlNormalizada, forzarActualizacion).pipe(
            catchError(() => of(respuesta))
          );
        }),
        catchError(() =>
          this.descubrirArcGisRestConAsociacionesBd(urlNormalizada, forzarActualizacion).pipe(
            catchError(() => of({ state: 'fail', layers: [], message: '' }))
          )
        )
      );
    }

    // GeoServer / WMS: flujo original sin cambios
    return discover$;
  }

  private descubrirServidorGet(url: string, forzarActualizacion = false): Observable<any> {
    const webConfig = JSON.parse(sessionStorage.getItem('webConfig'));
    const urlEncode = encodeURIComponent(url);
    let urlCompleta = webConfig.proxy + webConfig.descubrirServidor + urlEncode;

    if (forzarActualizacion) {
      urlCompleta += `?nocache=${Date.now()}`;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Content-Types': this.seguridadGlobal.atributoSesion,
    };

    if (forzarActualizacion) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
    }

    return this.http.get(urlCompleta, {
      headers: new HttpHeaders(headers),
    });
  }

  /**
   * Respaldo solo ArcGIS: lee ?f=json del MapServer.
   * 1) vía proxy (como el resto del módulo)
   * 2) si el proxy no alcanza el host (200 con status:error), directo desde el navegador
   * No aplica a GeoServer.
   */
  private descubrirArcGisRestDesdeMetadata(serviceUrl: string): Observable<any> {
    const base = normalizarUrlServicioArcGis(serviceUrl);
    const metadataUrl = base + '?f=json';

    const desdeMetadata = (metadata: any) => {
      if (!esMetadataArcGisValida(metadata)) {
        throw new Error('metadata ArcGIS no válida');
      }
      return construirDiscoverDesdeMetadataArcGis(metadata, base);
    };

    return this.query(metadataUrl, 'json').pipe(
      map(desdeMetadata),
      catchError(() =>
        // Proxy caído/inaccesible o JSON de error con HTTP 200 → intento directo (SGC es público)
        this.http.get<any>(metadataUrl).pipe(map(desdeMetadata))
      )
    );
  }

  /**
   * Catálogo ArcGIS (?f=json) + overlay de capas asociadas en BD vía filterLayers/allLayers.
   * Evita que el fallback muestre todo como "No asociadas" tras un associateLayer OK.
   */
  private descubrirArcGisRestConAsociacionesBd(
    serviceUrl: string,
    forzarActualizacion = false
  ): Observable<any> {
    const base = normalizarUrlServicioArcGis(serviceUrl);
    return this.descubrirArcGisRestDesdeMetadata(base).pipe(
      switchMap((catalogo) =>
        this.enriquecerDiscoverConAsociadasBd(catalogo, base, forzarActualizacion)
      )
    );
  }

  private enriquecerDiscoverConAsociadasBd(
    catalogo: any,
    serviceUrl: string,
    forzarActualizacion = false
  ): Observable<any> {
    const base = normalizarUrlServicioArcGis(serviceUrl);
    const token = extraerTokenFiltroServicio(base);
    if (!token) {
      return of(catalogo);
    }

    const mismaCapaServicio = (capa: any) => {
      const ser = normalizarUrlServicioArcGis(capa?.serviceUrl || '');
      if (ser && ser.toLowerCase() === base.toLowerCase()) {
        return true;
      }
      const urlCapa = normalizarUrlServicioArcGis(capa?.url || '');
      return !!urlCapa && urlCapa.toLowerCase().startsWith(base.toLowerCase() + '/');
    };

    const fusionarDesdeRespuesta = (respuesta: any) => {
      const candidatas = (respuesta?.layers || []).filter(mismaCapaServicio);
      return fusionarDiscoverConAsociadas(catalogo, candidatas);
    };

    const tieneAsociadas = (payload: any) =>
      (payload?.layers || []).some(
        (l: any) => l?.state === true || (l?.code != null && String(l.code).indexOf('CAP_') === 0)
      );

    // Tras Guardar (forzarActualizacion) hay que evitar disk-cache de filter/allLayers:
    // si no, una capa recién disociada vuelve a pintarse como Asociada.
    return this.GetCapasFiltradas(encodeURIComponent(token), forzarActualizacion).pipe(
      map(fusionarDesdeRespuesta),
      switchMap((fusionado) => {
        if (tieneAsociadas(fusionado)) {
          return of(fusionado);
        }
        return this.GetAllLayers(forzarActualizacion).pipe(
          map(fusionarDesdeRespuesta),
          catchError(() => of(fusionado))
        );
      }),
      catchError(() =>
        this.GetAllLayers(forzarActualizacion).pipe(
          map(fusionarDesdeRespuesta),
          catchError(() => of(catalogo))
        )
      )
    );
  }

  /**
   * Obtiene las estadísticas de los servicios
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  GetEstadisticasServicios(): any {
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).estadisticasServicios, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Obtiene las categorías de las capas
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  GetCategorias(): any {
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).categorias, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Obtiene las capas, según un filtro dado
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  GetCapasFiltradas(find: string, forzarActualizacion = false): any {
    const webConfig = JSON.parse(sessionStorage.getItem('webConfig'));
    let urlCompleta = webConfig.proxy + webConfig.capasFiltradas + find;
    if (forzarActualizacion) {
      urlCompleta += (urlCompleta.indexOf('?') >= 0 ? '&' : '?') + `nocache=${Date.now()}`;
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Content-Types': this.seguridadGlobal.atributoSesion,
    };
    if (forzarActualizacion) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
    }
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders(headers),
    });
  }

  /**
   * Obtiene los servicios
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  GetServicios(): any {
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).servicios, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  

  /**
   * Hace un llamado al componente capas para mostrar la lista
   * @author Elisabeth Campuzano Jaramillo
   * @date 08/01/2022
   * @param
   * @returns
   */
  LlenarCapas(capasFiltradas, mensaje) {
    this.capas.LlenarListaCapas(capasFiltradas, mensaje);
  }

  /**
   * Permite hacer una petición http dada una url y un tipo de respuesta esperado
   * @param url url del servicio que se va a consultar
   * @param respuestaEsperada tipo de respuesta que se espera de dicha consulta
   * @returns un observable con la respuesta del servicio
   */
  query(url: string, respuestaEsperada: any): Observable<any> {
    const headersT = new HttpHeaders({ "Proxy": "true", 'Content-Types': this.seguridadGlobal.atributoSesion });
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + url, {
      headers: headersT,
      responseType: respuestaEsperada,
    });
  }

  /**
   * Permite crear una uri que se usa para hacer una petición http
   * @param layer nombre de la capa
   * @param bbox Cuadro delimitador para la extensión del mapa
   * @returns la uri que se puede usar para hacer una petición http
   */
  getParamGeoserver(layer: string, bbox: string) {
    return L.Util.getParamString({
      request: "GetMap",
      service: "WMS",
      version: "2.0.0",
      layers: layer,
      srs: "EPSG:4326",
      bbox: bbox,
      width: "300",
      height: "200",
      format: "image/png",
    });
  }

  /**
   * Permite crear una uri que se usa para hacer una petición http
   * @param layer nombre de la capa
   * @param bbox Cuadro delimitador para la extensión del mapa
   * @returns la uri que se puede usar para hacer una petición http
   */
  getParamArcgis(layer: string, bbox: string, spatialReference: string) {
    return L.Util.getParamString({
      bbox: bbox,
      format: "png32",
      width: "300",
      height: "200",
      dpi: "96",
      transparent: "true",
      layers: "show:" + layer,
      bboxSR: spatialReference,
      imageSR: spatialReference,
      size: "300,200",
      f: "image"
    });
  }

  /**
   * Asocia la capa seleccionada por el usuario a la base de datos.
   * @author Elisabeth Campuzano Jaramillo
   * @date 17/01/2022
   * @param entrada
   * @returns
   */
  AsociarCapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.post(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).asociarCapa, entrada, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        accept: "text/plain",
        'Content-Types': this.seguridadGlobal.atributoSesion
      }),
    });
  }

  /**
   * Disocia la capa seleccionada por el usuario.
   * @author Elisabeth Campuzano Jaramillo
   * @date 17/01/2022
   * @param entrada
   * @returns
   */
  DisociarCapa(code) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.delete(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).disociarCapa + code, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Edita la capa seleccionada por el usuario.
   * @author Elisabeth Campuzano Jaramillo
   * @date 17/01/2022
   * @param entrada
   * @returns
   */
  EditarCapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.put(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).editarCapa, entrada, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        accept: "text/plain",
        'Content-Types': this.seguridadGlobal.atributoSesion
      }),
    });
  }

  /**
   * Edita la infocapa seleccionada por el usuario.
   * @author Elisabeth Campuzano Jaramillo
   * @date 17/01/2022
   * @param entrada
   * @returns
   */
  EditarInfoCapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.put(JSON.parse(sessionStorage.getItem('webConfig')).editarInfoCapa, entrada, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        accept: "text/plain",
        'Content-Types': this.seguridadGlobal.atributoSesion
      }),
    });
  }

  /**
   * Obtiene los dominios para poblar los select del grid
   * @author Elisabeth Campuzano Jaramillo
   * @date 13/02/2022
   * @param find dominio a buscar
   */
  ObtenerDominiosCapas(find: string) {
    // return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).descubrirServidor + urlEncode, { 
    //   headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion }) 
    // });
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).obtenerDominiosCapas + find, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
 * Obtiene los dominios para poblar los select del grid
 * @author Elisabeth Campuzano Jaramillo
 * @date 13/02/2022
 * @param find dominio a buscar
 */
  ObtenerDominiosCapasObj(find: string) {
    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).obtenerDominiosCapasObj + find, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Obtiene todas las capas almacenadas en base de datos
   * @author Lukas Mesa Buriticá
   * @date 20/02/2022
   */
  GetAllLayers(forzarActualizacion = false): any {
    const webConfig = JSON.parse(sessionStorage.getItem('webConfig'));
    let urlCompleta = webConfig.proxy + webConfig.obtenerTodas;
    if (forzarActualizacion) {
      urlCompleta += `?nocache=${Date.now()}`;
    }
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Content-Types': this.seguridadGlobal.atributoSesion,
    };
    if (forzarActualizacion) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
    }
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders(headers),
    });
  }

  /**
   * Obtiene los campos de una capa
   * @author Santiago Hernandez
   * @date 27/02/2022
   * @param
   * @returns
   */
  ObtenerCampos(url: string, urlCapa: string): Observable<any> {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "GET");
    headersT.append("Access-Control-Allow-Origin", "*");

    const urlEncode = encodeURIComponent(urlCapa);
    const urlCompleta = JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).urlOuterLayer + url + urlEncode;
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }


  /**
   * Se encarga de actualizar el cache del visor sobre los cambios realizados en gestión de capas
   * @author Nicolas Cardona
   * @date 2024-02-14
   * @param url 
   * @returns 
   */
  ActualizarCacheVisor(idVisor: string){
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "GET");
    headersT.append("Access-Control-Allow-Origin", "*");

    // Mismo contrato de producción: urlMap + loadCache/{idVisor}
    // (vía proxy como el resto de llamadas del módulo, para CORS en local/publicado)
    const webConfig = JSON.parse(sessionStorage.getItem('webConfig'));
    const urlCompleta = webConfig.proxy + webConfig.urlMap + "loadCache/" + idVisor;
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Obtiene las estadísticas del mapa
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param 
   * @returns
   */
  cargarEstadisticasMapa(mapa: string): Observable<any> {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.get(JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).estadisticasServicios + "/" + mapa, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }
  
  /**
   * Realiza una sigma consulta 
   * @author Elisabeth Campuzano
   * @date 15/09/2025
   * @returns 
   */
  getServicioSigmaConsulta(sistema, idFuncion, filtro, datosPeticion): Observable<any> {
    this.seguridadGlobal.validarSesion();
    return this.http.post(
      JSON.parse(sessionStorage.getItem('webConfig'))["servicioFw"] + "/FUNSigmaConsulta/ObtenerInformacion",
      {
        sistema: sistema,
        idFuncion: idFuncion,
        filtro: filtro,
        datosPeticion: datosPeticion
      },
      { headers: new HttpHeaders({ "Content-Type": "application/json", "Content-Types": this.seguridadGlobal.atributoSesion }) }
    );
  }

  /**
   * Obtiene las capas asociadas a un mapa 
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param map_codigo código del mapa
   * @returns
   */
  getCapasMapa(map_codigo: string): Observable<any> {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "GET");
    headersT.append("Access-Control-Allow-Origin", "*");

    const urlCompleta = JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).obtenerCapasxMapa + map_codigo;
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Obtiene los mapas a los que está asociada una capa 
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param cap_codigo código de la capa
   * @returns
   */
  getMapasCapa(cap_codigo: string): Observable<any> {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "GET");
    headersT.append("Access-Control-Allow-Origin", "*");

    const urlCompleta = JSON.parse(sessionStorage.getItem('webConfig')).proxy + JSON.parse(sessionStorage.getItem('webConfig')).obtenerMapasxCapa + cap_codigo;
    return this.http.get(urlCompleta, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion })
    });
  }

  /**
   * Asocia una capa a un mapa con su configuración
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param entrada
   * @returns
   */
  AsociarCapaxMapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.post(JSON.parse(sessionStorage.getItem('webConfig')).asociarCapaxMapa, entrada, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        accept: "text/plain",
        'Content-Types': this.seguridadGlobal.atributoSesion
      }),
    });
  }

  /**
   * Edita la configuración de una capa en un mapa
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param entrada
   * @returns
   */
  EditarCapaxMapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.put(JSON.parse(sessionStorage.getItem('webConfig')).editarCapaxMapa, entrada, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        accept: "text/plain",
        'Content-Types': this.seguridadGlobal.atributoSesion
      }),
    });
  }

  /**
   * Elimina una capa de un mapa
   * @author Elisabeth Campuzano Jaramillo
   * @date 15/09/2025
   * @param entrada
   * @returns
   */
  EliminarCapaxMapa(entrada) {
    const headersT = new HttpHeaders();
    headersT.append("Access-Control-Allow-Headers", "Content-Type");
    headersT.append("Access-Control-Allow-Methods", "POST");
    headersT.append("Access-Control-Allow-Origin", "*");

    return this.http.request('DELETE', JSON.parse(sessionStorage.getItem('webConfig')).eliminarCapaxMapa, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Content-Types': this.seguridadGlobal.atributoSesion }),
      body: entrada,
    });

  }
}
